import { useCallback, useEffect, useState } from 'react'
import { useMatches, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Container,
  FormDivider,
  PageSubtitle,
  PageTitle,
  TextHeaderBox,
  PageHeaderBox,
  ButtonHeaderBox,
} from './styles'

import { DashboardhMediumButton } from 'bd-web-components'

import { SECONDARY_BLUE_01 } from 'bd-web-components/src/utils/dash-colors'

import Loader from '../../components/Loader'
import { useGuard } from '../../hooks/auth'
import { useSpotify } from '../../hooks/spotify'
import { SpotifyForm, SpotifyData } from './components'
import { Breadcrumb } from '../../components/Breadcrumb'

export function Spotify() {
  useGuard(['DP10'])

  const { id } = useParams<{ id: string }>()

  // Estado para controlar se o Spotify já está configurado para este evento
  const [hasSpotifyConfig, setHasSpotifyConfig] = useState<boolean>(false)
  const [spotifyData, setSpotifyData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isEditing, setIsEditing] = useState<boolean>(false)

  // Hook do Spotify
  const { getSpotifyEvent, createSpotifyEvent } = useSpotify()

  let matches = useMatches()
  let crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => {
      return {
        name: match.handle.crumb(match.data),
        path: match.pathname,
      }
    })

  // Função para verificar se o Spotify está configurado
  const checkSpotifyConfig = useCallback(async () => {
    try {
      setIsLoading(true)

      if (id) {
        const response = await getSpotifyEvent(id)

        if (response && typeof response === 'object') {
          setHasSpotifyConfig(true)
          setSpotifyData(response)
        } else {
          setHasSpotifyConfig(false)
        }
      } else {
        setHasSpotifyConfig(false)
      }
    } catch (error) {
      console.error('Erro ao verificar configuração do Spotify:', error)
      setHasSpotifyConfig(false)
    } finally {
      setIsLoading(false)
    }
  }, [id, getSpotifyEvent])

  // Função para configurar o Spotify
  const handleSpotifyConfig = useCallback(
    async (formData: any) => {
      try {
        setIsLoading(true)

        if (id) {
          // Separar artistas principais dos artistas de apoio
          const mainArtists = formData.artists.filter(
            (artist: any) => artist.actType === 'HEADLINER'
          )
          const supportingArtists = formData.artists.filter(
            (artist: any) => artist.actType === 'SUPPORT'
          )

          const payload = {
            eventId: id,
            artists: mainArtists.map((artist: any) => ({
              spotifyId: artist.spotifyArtistId,
              name: artist.artistName,
            })),
            artistSupportings: supportingArtists.map((artist: any) => ({
              spotifyId: artist.spotifyArtistId,
              name: artist.artistName,
            })),
            eventCategory: formData.event_category,
          }

          const response = await createSpotifyEvent(payload)
          setSpotifyData(response)
          setHasSpotifyConfig(true)
          setIsEditing(false)
          toast.success(
            isEditing
              ? 'Spotify atualizado com sucesso!'
              : 'Spotify configurado com sucesso!'
          )
        }
      } catch (error) {
        console.error('Erro ao configurar Spotify:', error)
        toast.error('Erro ao configurar Spotify. Tente novamente.')
      } finally {
        setIsLoading(false)
      }
    },
    [id, createSpotifyEvent, isEditing]
  )

  // Função para preparar dados para edição
  const prepareDataForEdit = useCallback(() => {
    if (spotifyData) {
      // Transformar dados do Spotify para o formato esperado pelo formulário
      const artists = [
        ...(spotifyData.artists?.map((artist: any) => ({
          artistId: artist.spotifyId,
          artistName: artist.name,
          spotifyArtistId: artist.spotifyId,
          actType: 'HEADLINER' as const,
        })) || []),
        ...(spotifyData.artistSupportings?.map((artist: any) => ({
          artistId: artist.spotifyId,
          artistName: artist.name,
          spotifyArtistId: artist.spotifyId,
          actType: 'SUPPORT' as const,
        })) || []),
      ]

      return {
        artists,
        event_category: spotifyData.eventCategory as 'CONCERT' | 'FESTIVAL',
      }
    }
    return undefined
  }, [spotifyData])

  useEffect(() => {
    checkSpotifyConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Container>
        <Breadcrumb paths={[
          { name: 'Início', path: '/events' },
          { name: 'Evento', path: `/events/${id}` },
          ...crumbs,
        ]} />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageTitle>Spotify</PageTitle>
            <PageSubtitle>
              {hasSpotifyConfig && !isEditing
                ? 'Gerencie as configurações do Spotify para este evento.'
                : 'Configure o Spotify para este evento.'}
            </PageSubtitle>
          </TextHeaderBox>
          {hasSpotifyConfig && !isEditing && (
            <ButtonHeaderBox>
              <DashboardhMediumButton
                onClick={() => setIsEditing(true)}
                color="#fff"
                background={SECONDARY_BLUE_01}
                height="48px"
                maxWidth="100%"
                variant="contained"
                width="204px"
              >
                Editar configuração
              </DashboardhMediumButton>
            </ButtonHeaderBox>
          )}
        </PageHeaderBox>
        <FormDivider />

        {isLoading ? (
          <Loader />
        ) : hasSpotifyConfig && !isEditing ? (
          <>{spotifyData && <SpotifyData data={spotifyData} />}</>
        ) : (
          <>
            <SpotifyForm
              onSubmit={handleSpotifyConfig}
              initialData={isEditing ? prepareDataForEdit() : undefined}
              isEditing={isEditing}
              onCancel={isEditing ? () => setIsEditing(false) : undefined}
            />
          </>
        )}
      </Container>
    </>
  )
}
